/**
 * Shaping for the granted-profiles screen, kept out of the component so the parts that make a
 * claim about someone's access can be tested without rendering.
 *
 * A granted profile is elevated access for Orkes staff: `CLOUD_SUPPORT_ENGINEER_READ` and
 * `_WRITE` unlock the support surface across customer clusters, and `ENGINEERING` additionally
 * carries `globalBypass`. So this screen answers "who can currently reach customer
 * infrastructure", and a wrong answer here is worse than no answer.
 *
 * Two rules from the server that the display has to honour:
 *
 *   - `ENGINEERING` never lapses, whatever validity is recorded against it. Showing an expiry for
 *     it would suggest the access ends on its own, which it does not.
 *   - Only active grants are returned, so a lapsed grant is still a live row. It has to stay
 *     visible and be marked, since a lapsed grant nobody revoked is precisely what an audit is
 *     looking for.
 */

export interface GrantedProfileApplyTo {
  org?: string;
  clusters?: string[];
}

export interface GrantedProfile {
  id: string;
  userId?: string;
  userEmail?: string;
  userName?: string;
  profile?: string;
  grantedBy?: string;
  grantedByEmail?: string;
  assignedAt?: string;
  validitySeconds?: number;
  validUntil?: string;
  valid?: boolean | null;
  appliesTo?: GrantedProfileApplyTo[];
}

export const PROFILES = [
  "CLOUD_SUPPORT_ENGINEER_READ",
  "CLOUD_SUPPORT_ENGINEER_WRITE",
  "ENGINEERING",
] as const;

/** The profile that carries globalBypass, and so the one worth marking differently. */
export const UNRESTRICTED_PROFILE = "ENGINEERING";

export const VALIDITY_OPTIONS = [
  { label: "1 hour", seconds: 3600 },
  { label: "8 hours", seconds: 8 * 3600 },
  { label: "1 day", seconds: 24 * 3600 },
  { label: "7 days", seconds: 7 * 24 * 3600 },
  { label: "30 days", seconds: 30 * 24 * 3600 },
] as const;

export type Standing = "active" | "lapsed" | "permanent" | "unknown";

/**
 * Where a grant stands right now.
 *
 * `permanent` is deliberately distinct from `active`: both mean the holder has access, but only
 * one of them will stop on its own, and an auditor needs to tell them apart.
 */
export function standingOf(grant: GrantedProfile): Standing {
  if (grant.profile === UNRESTRICTED_PROFILE) {
    return "permanent";
  }
  if (grant.valid === true) {
    return "active";
  }
  if (grant.valid === false) {
    return "lapsed";
  }
  return "unknown";
}

export function describeStanding(standing: Standing): string {
  switch (standing) {
    case "permanent":
      return "Does not expire";
    case "active":
      return "Active";
    case "lapsed":
      return "Lapsed, not revoked";
    default:
      return "Unknown";
  }
}

/** Colour by consequence: a lapsed grant that nobody revoked is the one to look at. */
export function standingSeverity(
  standing: Standing
): "success" | "warning" | "error" | "default" {
  switch (standing) {
    case "permanent":
      return "error";
    case "active":
      return "success";
    case "lapsed":
      return "warning";
    default:
      return "default";
  }
}

export function formatValidUntil(grant: GrantedProfile): string {
  if (grant.profile === UNRESTRICTED_PROFILE) {
    return "Never";
  }
  if (!grant.validUntil) {
    return "-";
  }
  const at = new Date(grant.validUntil);
  return Number.isNaN(at.getTime()) ? "-" : at.toLocaleString();
}

export function formatAssignedAt(grant: GrantedProfile): string {
  if (!grant.assignedAt) {
    return "-";
  }
  const at = new Date(grant.assignedAt);
  return Number.isNaN(at.getTime()) ? "-" : at.toLocaleString();
}

/** "*" is the all-organizations form the server writes; spell it out rather than showing a star. */
export function formatAppliesTo(grant: GrantedProfile): string {
  const entries = grant.appliesTo ?? [];
  if (entries.length === 0) {
    return "-";
  }
  if (entries.some((e) => e.org === "*")) {
    return "All organizations";
  }
  return entries
    .map((e) => {
      const clusters = e.clusters ?? [];
      return clusters.length > 0
        ? `${e.org} (${clusters.join(", ")})`
        : e.org ?? "";
    })
    .filter(Boolean)
    .join("; ");
}

/**
 * Lapsed grants first, then permanent, then active, and alphabetical within each.
 *
 * The ordering is the point of the screen rather than a nicety: the rows that need action are the
 * ones still granting access that nobody intended to be standing.
 */
export function sortGrants(grants: GrantedProfile[]): GrantedProfile[] {
  const rank: Record<Standing, number> = {
    lapsed: 0,
    permanent: 1,
    active: 2,
    unknown: 3,
  };
  return [...grants].sort((a, b) => {
    const byStanding = rank[standingOf(a)] - rank[standingOf(b)];
    if (byStanding !== 0) {
      return byStanding;
    }
    return (a.userEmail ?? "").localeCompare(b.userEmail ?? "");
  });
}

/** Only Orkes staff can hold a profile; the server enforces it, and the form should say so early. */
export function isGrantableEmail(email: string): boolean {
  return /^[^@\s]+@orkes\.io$/i.test(email.trim());
}
